import React, { createContext, useContext, useMemo } from 'react';
import { IAtsConnection, useGetAtsConnections } from '@/core/protected/Integrations/Ats/api/query';
import useAtsNotificationsStream from '@/core/protected/Integrations/Ats/hooks/useAtsNotificationsStream';
import { lockCampaign, unlockCampaign } from '@/core/protected/JobCampaigns/hooks/campaignLockStore';
import { GetStorageComponent } from '@/utils/session';

interface AtsContextValue {
  atsData: IAtsConnection[] | undefined;
}

const AtsContext = createContext<AtsContextValue | null>(null);

export const AtsProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: atsData } = useGetAtsConnections();
  const connections = atsData?.data;

  // Own the company-wide notifications stream here (not in the Sync button) so it stays connected on
  // every protected route — it patches the connections cache (the "New" badge) and feeds the campaign
  // lock store (job_locked / job_unlocked) that gates campaign mutations across pages.
  //
  // companyId comes from the logged-in user, NOT the ATS connection: job_locked fires on plain candidate
  // uploads too, so a company with no ATS integrated must still receive lock events.
  const companyId = useMemo<number | undefined>(() => {
    try {
      const user = JSON.parse(GetStorageComponent('user') || '{}');
      return user?.company?.id;
    } catch {
      return undefined;
    }
  }, []);
  useAtsNotificationsStream(companyId, Boolean(companyId), {
    onJobLocked: data => lockCampaign({ jobId: data.jobId, flow: data.flow, startedAt: data.startedAt }),
    onJobUnlocked: data => unlockCampaign(data.jobId),
  });

  return <AtsContext.Provider value={{ atsData: connections }}>{children}</AtsContext.Provider>;
};

export const useAts = (): AtsContextValue => {
  const ctx = useContext(AtsContext);
  if (!ctx) throw new Error('useAts must be used inside AtsProvider');
  return ctx;
};
